/**
 * Ported from Floating UI's `safePolygon` close handler.
 * https://github.com/floating-ui/floating-ui/blob/master/packages/react/src/safePolygon.ts
 * MIT License — Copyright (c) 2021-present Floating UI contributors.
 *
 * Adaptations for the cf-web popover primitive:
 * - no FloatingTree / nested floating element support
 * - `placement` replaced by a plain `side` (read from Radix's `data-side`)
 * - opt-in `blockPointerEvents` support for clustered hover menus, where a
 *   sibling trigger must not steal the interaction inside the safe polygon
 * - optional `debug` callback to visualise the polygon (`?showSafeArea`)
 * - the handler exposes `leave(event)` for the initial leave invocation.
 *   Floating UI infers the leave from `event.type === 'mouseleave'`, but
 *   React's synthetic `onMouseLeave` wraps a native `mouseout` event, so the
 *   type check can never match — the leave must be signalled explicitly
 * - the factory exposes `cancel()` to clear the pending rest-timeout close and
 *   reset cursor intent between open sessions.
 *   Floating UI keeps its mousemove listener attached after the cursor lands
 *   on the floating element (the next tick clears the timeout); we detach the
 *   listener on content mouseenter instead, so the timeout must be cleared
 *   explicitly or it would close the popover ~40ms after landing
 *
 * How it works: when the cursor leaves the trigger, the returned handler runs
 * on every document mousemove. It keeps the popover open while the cursor is
 * inside a polygon spanning from the cursor's exit point to the floating
 * element (plus the rectangular trough between trigger and content). It closes
 * when the cursor exits the polygon, moves too slowly toward the content
 * (`requireIntent`), or rests for 40ms without having reached the content.
 */

export type Point = [number, number]
export type Polygon = Point[]
export type Side = 'top' | 'right' | 'bottom' | 'left'

export interface SafePolygonContext {
  /** Cursor position where the mouse left the trigger/content. */
  x: number
  y: number
  side: Side
  reference: Element
  floating: Element
  onClose: () => void
}

export interface SafePolygonOptions {
  /** Amount of buffer (in pixels) around the polygon. */
  buffer?: number
  /**
   * Maximum outward curve (in pixels) along the two diagonal edges. The
   * tolerance tapers to zero at the cursor and floating-element corners.
   */
  bulge?: number
  /** Time without mouse movement before an unfinished traversal closes. */
  restMs?: number
  /**
   * Temporarily disables pointer events outside the reference and floating
   * elements while the cursor traverses the safe polygon. This prevents a
   * sibling hover trigger underneath the polygon from stealing the interaction.
   */
  blockPointerEvents?: boolean
  /**
   * Whether the cursor must move quickly enough toward the floating element
   * for the triangle polygon to apply. Prevents nearby triggers from being
   * blocked for slow, aimless movement.
   */
  requireIntent?: boolean
  /** Debug visualisation hook; receives the current polygons or null. */
  debug?: (polygons: Polygon[] | null) => void
}

function isPointInPolygon(point: Point, polygon: Polygon) {
  const [x, y] = point
  let isInside = false
  const length = polygon.length
  for (let i = 0, j = length - 1; i < length; j = i++) {
    const [xi, yi] = polygon[i] || [0, 0]
    const [xj, yj] = polygon[j] || [0, 0]
    const intersect = yi >= y !== yj >= y && x <= ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) {
      isInside = !isInside
    }
  }
  return isInside
}

function isInsideRect(point: Point, rect: DOMRect) {
  return (
    point[0] >= rect.x &&
    point[0] <= rect.x + rect.width &&
    point[1] >= rect.y &&
    point[1] <= rect.y + rect.height
  )
}

function isElement(value: unknown): value is Element {
  return typeof Element !== 'undefined' && value instanceof Element
}

function contains(parent: Element | null | undefined, child: Element | null) {
  if (!parent || !child) {
    return false
  }
  return parent === child || parent.contains(child)
}

function getTarget(event: MouseEvent): EventTarget | null {
  return event.composedPath()[0] ?? event.target
}

function clearTimeoutIfSet(timeoutRef: { current: number }) {
  if (timeoutRef.current !== -1) {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = -1
  }
}

function getBulgedEdge(start: Point, end: Point, offset: Point): Point[] {
  if (offset[0] === 0 && offset[1] === 0) {
    return []
  }

  const control: Point = [
    (start[0] + end[0]) / 2 + offset[0] * 2,
    (start[1] + end[1]) / 2 + offset[1] * 2,
  ]

  // Three interior points are enough to make the debug polygon look curved
  // while keeping pointer hit testing cheap on mousemove.
  return [0.25, 0.5, 0.75].map(t => {
    const remaining = 1 - t
    return [
      remaining * remaining * start[0] + 2 * remaining * t * control[0] + t * t * end[0],
      remaining * remaining * start[1] + 2 * remaining * t * control[1] + t * t * end[1],
    ]
  })
}

export interface SafePolygonHandlerFactory {
  (context: SafePolygonContext): SafePolygonHandler
  /** Clears transient cursor state (call when detaching the handler). */
  cancel(): void
  /** Limits hit testing to the active trigger and popover. */
  blockOutsidePointerEvents(reference: Element, floating: Element): void
  /** Restores hit testing after the popover closes. */
  unblockOutsidePointerEvents(): void
}

export interface SafePolygonHandler {
  (event: MouseEvent): void
  /** Runs the handler for the initial React mouse-leave event. */
  leave(event: MouseEvent): void
}

// Body-level pointer-events guard, reference-counted across every safePolygon
// instance in the process. Uses a data attribute + injected <style> rule
// instead of manipulating body.style.pointerEvents directly. This avoids
// collisions with Radix Dialog/Sheet which independently snapshot and restore
// the same inline property — if both systems wrote to body.style.pointerEvents
// the last-to-close would restore the other's "none" and strand the page.
//
// With the data-attribute approach, Radix's inline-style snapshot/restore never
// sees our guard (we don't touch body.style.pointerEvents), and our guard never
// sees theirs (we only toggle an attribute). Per-element inline pointer-events
// on trigger/content override the inherited body value normally.
const BODY_POINTER_BLOCK_ATTR = 'data-popover-pointer-block'
let bodyPointerEventsGuardCount = 0
let guardStyleElement: HTMLStyleElement | null = null

function ensureGuardStyle(): void {
  if (typeof document === 'undefined') {
    return
  }
  if (guardStyleElement?.parentNode) {
    return
  }
  guardStyleElement = document.createElement('style')
  guardStyleElement.textContent = `body[${BODY_POINTER_BLOCK_ATTR}]{pointer-events:none}`
  document.head.appendChild(guardStyleElement)
}

function acquireBodyPointerEventsGuard(): void {
  if (typeof document === 'undefined') {
    return
  }
  if (bodyPointerEventsGuardCount === 0) {
    ensureGuardStyle()
    document.body.dataset.popoverPointerBlock = ''
  }
  bodyPointerEventsGuardCount++
}

function releaseBodyPointerEventsGuard(): void {
  if (typeof document === 'undefined' || bodyPointerEventsGuardCount === 0) {
    return
  }
  bodyPointerEventsGuardCount--
  if (bodyPointerEventsGuardCount === 0) {
    delete document.body.dataset.popoverPointerBlock
  }
}

/**
 * Generates a safe polygon area that the user can traverse without closing the
 * floating element once leaving the reference element.
 *
 * Create one instance per popover (cursor-intent state is kept in the
 * closure), then call it on each mouseleave to produce a mousemove handler.
 */
export function safePolygon(options: SafePolygonOptions = {}): SafePolygonHandlerFactory {
  const {
    buffer = 0.5,
    bulge = 0,
    blockPointerEvents = false,
    requireIntent = true,
    restMs: configuredRestMs,
    debug,
  } = options
  const restMs = configuredRestMs ?? (requireIntent ? 40 : 0)

  const timeoutRef = { current: -1 }

  let hasLanded = false
  let lastX: number | null = null
  let lastY: number | null = null
  let lastCursorTime = typeof performance !== 'undefined' ? performance.now() : 0
  let restorePointerEvents: (() => void) | null = null
  let boundReference: Element | null = null
  let boundFloating: Element | null = null

  function enableOutsidePointerEventsBlock(reference: Element, floating: Element) {
    if (!blockPointerEvents || typeof document === 'undefined') {
      return
    }

    if (restorePointerEvents && boundReference === reference && boundFloating === floating) {
      return
    }

    // Bound to different elements (e.g. Content key changed while open) —
    // release the old binding so the previous elements' styles are restored
    // and the body guard count stays balanced.
    if (restorePointerEvents) {
      restorePointerEvents()
    }

    const referenceElement = reference as HTMLElement
    const floatingElement = floating as HTMLElement
    const referencePointerEvents = referenceElement.style.pointerEvents
    const floatingPointerEvents = floatingElement.style.pointerEvents

    acquireBodyPointerEventsGuard()
    referenceElement.style.pointerEvents = 'auto'
    floatingElement.style.pointerEvents = 'auto'
    boundReference = reference
    boundFloating = floating

    restorePointerEvents = () => {
      releaseBodyPointerEventsGuard()
      referenceElement.style.pointerEvents = referencePointerEvents
      floatingElement.style.pointerEvents = floatingPointerEvents
      restorePointerEvents = null
      boundReference = null
      boundFloating = null
    }
  }

  function getCursorSpeed(x: number, y: number): number | null {
    const currentTime = performance.now()
    const elapsedTime = currentTime - lastCursorTime

    if (lastX === null || lastY === null || elapsedTime === 0) {
      lastX = x
      lastY = y
      lastCursorTime = currentTime
      return null
    }

    const deltaX = x - lastX
    const deltaY = y - lastY
    const distance = Math.hypot(deltaX, deltaY)
    const speed = distance / elapsedTime // px / ms

    lastX = x
    lastY = y
    lastCursorTime = currentTime

    return speed
  }

  function createHandler({ x, y, side, reference, floating, onClose }: SafePolygonContext) {
    function handleMouseEvent(event: MouseEvent, isLeave: boolean) {
      function close() {
        clearTimeoutIfSet(timeoutRef)
        debug?.(null)
        onClose()
      }

      clearTimeoutIfSet(timeoutRef)

      const { clientX, clientY } = event
      const clientPoint: Point = [clientX, clientY]
      const target = getTarget(event) as Element | null
      const isOverFloatingEl = contains(floating, target)
      const isOverReferenceEl = contains(reference, target)
      const refRect = reference.getBoundingClientRect()
      const rect = floating.getBoundingClientRect()
      const cursorLeaveFromRight = x > rect.right - rect.width / 2
      const cursorLeaveFromBottom = y > rect.bottom - rect.height / 2
      const isOverReferenceRect = isInsideRect(clientPoint, refRect)
      const isFloatingWider = rect.width > refRect.width
      const isFloatingTaller = rect.height > refRect.height
      const left = (isFloatingWider ? refRect : rect).left
      const right = (isFloatingWider ? refRect : rect).right
      const top = (isFloatingTaller ? refRect : rect).top
      const bottom = (isFloatingTaller ? refRect : rect).bottom

      if (isOverFloatingEl) {
        hasLanded = true

        if (!isLeave) {
          debug?.(null)
          return
        }
      }

      if (isOverReferenceEl) {
        hasLanded = false
      }

      if (isOverReferenceEl && !isLeave) {
        hasLanded = true
        debug?.(null)
        return
      }

      // Prevent overlapping floating element from being stuck in an open-close
      // loop: https://github.com/floating-ui/floating-ui/issues/1910
      if (isLeave && isElement(event.relatedTarget) && contains(floating, event.relatedTarget)) {
        return
      }

      // If the pointer is leaving from the opposite side, the "buffer" logic
      // creates a point where the floating element remains open, but should be
      // ignored.
      // A constant of 1 handles floating point rounding errors.
      if (
        (side === 'top' && y >= refRect.bottom - 1) ||
        (side === 'bottom' && y <= refRect.top + 1) ||
        (side === 'left' && x >= refRect.right - 1) ||
        (side === 'right' && x <= refRect.left + 1)
      ) {
        return close()
      }

      // Ignore when the cursor is within the rectangular trough between the
      // two elements. Since the triangle is created from the cursor point,
      // which can start beyond the ref element's edge, traversing back and
      // forth from the ref to the floating element can cause it to close. This
      // ensures it always remains open in that case.
      let rectPoly: Point[] = []

      switch (side) {
        case 'top': {
          rectPoly = [
            [left, refRect.top + 1],
            [left, rect.bottom - 1],
            [right, rect.bottom - 1],
            [right, refRect.top + 1],
          ]
          break
        }
        case 'bottom': {
          rectPoly = [
            [left, rect.top + 1],
            [left, refRect.bottom - 1],
            [right, refRect.bottom - 1],
            [right, rect.top + 1],
          ]
          break
        }
        case 'left': {
          rectPoly = [
            [rect.right - 1, bottom],
            [rect.right - 1, top],
            [refRect.left + 1, top],
            [refRect.left + 1, bottom],
          ]
          break
        }
        case 'right': {
          rectPoly = [
            [refRect.right - 1, bottom],
            [refRect.right - 1, top],
            [rect.left + 1, top],
            [rect.left + 1, bottom],
          ]
          break
        }
      }

      function getPolygon([x, y]: Point): Polygon {
        switch (side) {
          case 'top': {
            const cursorPointOne: Point = [
              isFloatingWider
                ? x + buffer / 2
                : cursorLeaveFromRight
                  ? x + buffer * 4
                  : x - buffer * 4,
              y + buffer + 1,
            ]
            const cursorPointTwo: Point = [
              isFloatingWider
                ? x - buffer / 2
                : cursorLeaveFromRight
                  ? x + buffer * 4
                  : x - buffer * 4,
              y + buffer + 1,
            ]
            const commonPoints: [Point, Point] = [
              [
                rect.left,
                cursorLeaveFromRight
                  ? rect.bottom - buffer
                  : isFloatingWider
                    ? rect.bottom - buffer
                    : rect.top,
              ],
              [
                rect.right,
                cursorLeaveFromRight
                  ? isFloatingWider
                    ? rect.bottom - buffer
                    : rect.top
                  : rect.bottom - buffer,
              ],
            ]

            return [
              cursorPointOne,
              cursorPointTwo,
              ...getBulgedEdge(cursorPointTwo, commonPoints[0], [-bulge, 0]),
              commonPoints[0],
              commonPoints[1],
              ...getBulgedEdge(commonPoints[1], cursorPointOne, [bulge, 0]),
            ]
          }
          case 'bottom': {
            const cursorPointOne: Point = [
              isFloatingWider
                ? x + buffer / 2
                : cursorLeaveFromRight
                  ? x + buffer * 4
                  : x - buffer * 4,
              y - buffer,
            ]
            const cursorPointTwo: Point = [
              isFloatingWider
                ? x - buffer / 2
                : cursorLeaveFromRight
                  ? x + buffer * 4
                  : x - buffer * 4,
              y - buffer,
            ]
            const commonPoints: [Point, Point] = [
              [
                rect.left,
                cursorLeaveFromRight
                  ? rect.top + buffer
                  : isFloatingWider
                    ? rect.top + buffer
                    : rect.bottom,
              ],
              [
                rect.right,
                cursorLeaveFromRight
                  ? isFloatingWider
                    ? rect.top + buffer
                    : rect.bottom
                  : rect.top + buffer,
              ],
            ]

            return [
              cursorPointOne,
              cursorPointTwo,
              ...getBulgedEdge(cursorPointTwo, commonPoints[0], [-bulge, 0]),
              commonPoints[0],
              commonPoints[1],
              ...getBulgedEdge(commonPoints[1], cursorPointOne, [bulge, 0]),
            ]
          }
          case 'left': {
            const cursorPointOne: Point = [
              x + buffer + 1,
              isFloatingTaller
                ? y + buffer / 2
                : cursorLeaveFromBottom
                  ? y + buffer * 4
                  : y - buffer * 4,
            ]
            const cursorPointTwo: Point = [
              x + buffer + 1,
              isFloatingTaller
                ? y - buffer / 2
                : cursorLeaveFromBottom
                  ? y + buffer * 4
                  : y - buffer * 4,
            ]
            const commonPoints: [Point, Point] = [
              [
                cursorLeaveFromBottom
                  ? rect.right - buffer
                  : isFloatingTaller
                    ? rect.right - buffer
                    : rect.left,
                rect.top,
              ],
              [
                cursorLeaveFromBottom
                  ? isFloatingTaller
                    ? rect.right - buffer
                    : rect.left
                  : rect.right - buffer,
                rect.bottom,
              ],
            ]

            return [
              commonPoints[0],
              commonPoints[1],
              ...getBulgedEdge(commonPoints[1], cursorPointOne, [0, bulge]),
              cursorPointOne,
              cursorPointTwo,
              ...getBulgedEdge(cursorPointTwo, commonPoints[0], [0, -bulge]),
            ]
          }
          case 'right': {
            const cursorPointOne: Point = [
              x - buffer,
              isFloatingTaller
                ? y + buffer / 2
                : cursorLeaveFromBottom
                  ? y + buffer * 4
                  : y - buffer * 4,
            ]
            const cursorPointTwo: Point = [
              x - buffer,
              isFloatingTaller
                ? y - buffer / 2
                : cursorLeaveFromBottom
                  ? y + buffer * 4
                  : y - buffer * 4,
            ]
            const commonPoints: [Point, Point] = [
              [
                cursorLeaveFromBottom
                  ? rect.left + buffer
                  : isFloatingTaller
                    ? rect.left + buffer
                    : rect.right,
                rect.top,
              ],
              [
                cursorLeaveFromBottom
                  ? isFloatingTaller
                    ? rect.left + buffer
                    : rect.right
                  : rect.left + buffer,
                rect.bottom,
              ],
            ]

            return [
              cursorPointOne,
              cursorPointTwo,
              ...getBulgedEdge(cursorPointTwo, commonPoints[0], [0, -bulge]),
              commonPoints[0],
              commonPoints[1],
              ...getBulgedEdge(commonPoints[1], cursorPointOne, [0, bulge]),
            ]
          }
        }
      }

      debug?.([rectPoly, getPolygon([x, y])])

      if (isPointInPolygon([clientX, clientY], rectPoly)) {
        return
      }

      if (hasLanded && !isOverReferenceRect) {
        return close()
      }

      if (!isLeave && requireIntent) {
        const cursorSpeed = getCursorSpeed(event.clientX, event.clientY)
        const cursorSpeedThreshold = 0.1
        if (cursorSpeed !== null && cursorSpeed < cursorSpeedThreshold) {
          return close()
        }
      }

      if (!isPointInPolygon([clientX, clientY], getPolygon([x, y]))) {
        close()
      } else if (!hasLanded && restMs > 0) {
        timeoutRef.current = window.setTimeout(close, restMs)
      }
    }

    const onMouseMove: SafePolygonHandler = event => handleMouseEvent(event, false)
    onMouseMove.leave = event => handleMouseEvent(event, true)
    return onMouseMove
  }

  createHandler.cancel = () => {
    clearTimeoutIfSet(timeoutRef)
    hasLanded = false
    lastX = null
    lastY = null
    lastCursorTime = typeof performance !== 'undefined' ? performance.now() : 0
    debug?.(null)
  }

  createHandler.blockOutsidePointerEvents = enableOutsidePointerEventsBlock
  createHandler.unblockOutsidePointerEvents = () => restorePointerEvents?.()

  return createHandler
}
